import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Returns a handler that navigates to the logical parent of the current training route.
 * Uses path-based navigation instead of history.back() for predictable behavior.
 *
 * Route hierarchy:
 * - /training/courses/:courseId/sessions/:sessionId → /training/courses/:courseId
 * - /training/courses/:courseId → /training/courses
 * - /training/drills/:drillId/sessions/:sessionId → /training/drills/:drillId
 * - /training/drills/:drillId → /training/drills
 * - Fallback → /training/dashboard
 */
export function useTrainingBack() {
	const navigate = useNavigate();
	const { pathname } = useLocation();

	return useCallback(() => {
		// Strip leading slash and split
		const segments = pathname.replace(/^\/+/, "").split("/");

		// training, courses|drills, id?, sessions?, sessionId?
		if (segments[0] === "training" && segments[1] === "courses") {
			if (segments[2] && segments[3] === "sessions" && segments[4]) {
				// /training/courses/:courseId/sessions/:sessionId → /training/courses/:courseId
				navigate(`/training/courses/${segments[2]}`, { replace: true });
				return;
			}
			if (segments[2]) {
				// /training/courses/:courseId → /training/courses
				navigate("/training/courses", { replace: true });
				return;
			}
		}

		if (segments[0] === "training" && segments[1] === "drills") {
			if (segments[2] && segments[3] === "sessions" && segments[4]) {
				// /training/drills/:drillId/sessions/:sessionId → /training/drills/:drillId
				navigate(`/training/drills/${segments[2]}`, { replace: true });
				return;
			}
			if (segments[2]) {
				// /training/drills/:drillId → /training/drills
				navigate("/training/drills", { replace: true });
				return;
			}
		}

		// Fallback: go to dashboard
		navigate("/training/dashboard", { replace: true });
	}, [navigate, pathname]);
}
